import { useEffect } from 'react';
import { Box, IconButton, Paper, Slide, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { GoalAlert } from '../../hooks/useGoalAlerts';

/** How long a goal notification stays up before auto-dismissing. */
const GOAL_ALERT_MS = 8000;

function GoalToast({ alert, onDone }: { alert: GoalAlert; onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, GOAL_ALERT_MS);
    return () => clearTimeout(t);
  }, [onDone]);

  const scorer = alert.side === 'home' ? alert.home : alert.away;

  return (
    <Slide direction="down" in mountOnEnter unmountOnExit>
      <Paper
        elevation={12}
        role="alert"
        sx={{
          px: 2,
          py: 1.25,
          minWidth: 340,
          maxWidth: 460,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderRadius: 2,
          border: 2,
          borderColor: 'success.main',
          bgcolor: 'success.dark',
          color: 'common.white',
        }}
      >
        <Typography component="span" sx={{ fontSize: 30, lineHeight: 1 }}>
          ⚽
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 900, letterSpacing: 1, lineHeight: 1.1 }}>
            GOL — {scorer}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.95 }} noWrap>
            {alert.home} vs {alert.away}
            <Box component="span" sx={{ fontWeight: 800, ml: 1 }}>
              {alert.score}
            </Box>
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onDone}
          aria-label="dismiss goal alert"
          sx={{ color: 'common.white' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Paper>
    </Slide>
  );
}

/**
 * Fixed, top-centre stack of prominent GOAL notifications for the Live Match
 * Events hub. Non-blocking (goals fire across many matches, a true blocking modal
 * would interrupt constantly) but visually loud; each auto-dismisses after
 * {@link GOAL_ALERT_MS}, newest on top, and can be closed by hand.
 */
export function GoalAlertStack({
  alerts,
  onDismiss,
}: {
  alerts: GoalAlert[];
  onDismiss: (key: string) => void;
}) {
  if (alerts.length === 0) return null;
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: (t) => t.zIndex.snackbar + 1,
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: 1,
        alignItems: 'center',
        pointerEvents: 'none',
        '& > *': { pointerEvents: 'auto' },
      }}
    >
      {alerts.map((a) => (
        <GoalToast key={a.key} alert={a} onDone={() => onDismiss(a.key)} />
      ))}
    </Box>
  );
}
